import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Link } from 'react-router-dom';
import { DeleteEntryDialog } from '../components/DeleteEntryDialog.js';
import { FileGrid } from '../components/FileGrid.js';
import { FileTable } from '../components/FileTable.js';
import { SearchBox } from '../components/SearchBox.js';
import { StitchList } from '../components/StitchList.js';
import { SearchResults } from '../components/SearchResults.js';
import { UploadPanel } from '../components/UploadPanel.js';
import { useDebounced } from '../hooks/useDebounced.js';
import { useUploadQueue } from '../hooks/useUploadQueue.js';
import { ApiError, api } from '../lib/api.js';
import { useSession } from '../lib/session.jsx';
import type { BrowseResult, Entry, LinkTarget, SearchResponse } from '../lib/types.js';
import './DocumentsPage.css';

export function DocumentsPage() {
  const { me } = useSession();
  // Due assi distinti: `studio` cambia la navigazione, `mista` e `studio`
  // cambiano il contenuto. `mista` è la barra in alto con le cartelle a tile.
  // Con la sola grafica stitch questi tre erano rami di una scelta che non
  // esiste piu': restano come costanti per non riscrivere il JSX sotto.
  const richContent = true;
  const sidebarNav = true;
  const stitch = true;
  // `undefined` = root del cliente attivo.
  const [ref, setRef] = useState<string | undefined>(undefined);
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<'area' | 'folder'>('area');
  const queryClient = useQueryClient();

  // Una chiamata per tasto premuto sarebbe una chiamata a SharePoint per tasto
  // premuto. Sotto i 2 caratteri l'API rifiuta comunque: non la si disturba.
  const debouncedQ = useDebounced(q.trim(), 350);
  const searching = debouncedQ.length >= 2;
  // Alla radice i due ambiti coinciderebbero: si cerca in tutta l'area.
  const searchRef = scope === 'folder' ? ref : undefined;

  const browse = useQuery<BrowseResult>({
    queryKey: ['browse', me.activeClient?.id ?? null, ref ?? 'root'],
    queryFn: () => api.browse(ref),
    enabled: me.activeClient !== null,
  });

  const search = useQuery<SearchResponse>({
    queryKey: ['search', me.activeClient?.id ?? null, searchRef ?? 'area', debouncedQ],
    queryFn: () => api.search(debouncedQ, searchRef),
    enabled: searching && me.activeClient !== null,
    // I risultati invecchiano come l'indice di SharePoint: qualche secondo di
    // riuso evita di rifare la stessa ricerca tornando indietro.
    staleTime: 30_000,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['browse'] });
  }, [queryClient]);

  /*
   * Il nome della cartella corrente: l'ultima briciola di pane, che alla radice
   * e' il nome dell'area. Serve solo a scriverlo nel pannello dei caricamenti —
   * a decidere la destinazione e' il `ref`.
   */
  const dirName =
    browse.data?.breadcrumb.at(-1)?.name ?? me.activeClient?.displayName ?? 'Area documenti';

  const uploads = useUploadQueue(ref, dirName, invalidate);
  const canUpload = browse.data?.perms.upload ?? false;

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: (files) => {
      if (canUpload && files.length > 0) uploads.enqueue(files);
    },
    disabled: !canUpload,
    noClick: true,
    noKeyboard: true,
  });

  /** Aprire una cartella chiude la ricerca: si è trovato quel che si cercava. */
  const goToFolder = (folderRef: string) => {
    setRef(folderRef);
    setQ('');
  };

  const openFolder = (entry: Entry) => goToFolder(entry.ref);

  /*
   * Collegamenti `.url`.
   *
   * La destinazione sta dentro il file, su SharePoint: va chiesta al server, che
   * la legge e rifiuta tutto quello che non e' http/https. Quindi c'e' un
   * `await` fra il click e la navigazione, e questo obbliga a due accortezze:
   *
   *  1. la scheda si apre **subito**, dentro il gesto dell'utente. Aprirla dopo
   *     la risposta la farebbe passare per un popup, e il browser la bloccherebbe;
   *  2. `opener = null` prima di navigare: la pagina di destinazione non deve
   *     poter rimandare altrove la scheda del portale (tabnabbing).
   *
   * Se il popup e' bloccato lo stesso (impostazione rigida, o un browser che non
   * accetta la scheda vuota) non si perde l'azione: si mostra l'indirizzo con un
   * link vero, che e' un gesto diretto e passa sempre.
   */
  const [linkBloccato, setLinkBloccato] = useState<LinkTarget | null>(null);
  const [linkErrore, setLinkErrore] = useState<string | null>(null);

  const openLink = useCallback(async (entry: { ref: string; name: string }) => {
    const scheda = window.open('', '_blank');
    setLinkErrore(null);
    try {
      const target = await api.link(entry.ref);
      if (scheda) {
        scheda.opener = null;
        scheda.location.replace(target.url);
      } else {
        setLinkBloccato(target);
      }
    } catch (err) {
      scheda?.close();
      setLinkErrore(
        err instanceof ApiError ? err.message : 'Collegamento non apribile.',
      );
    }
  }, []);

  // La cancellazione passa da una conferma: su SharePoint l'operazione la esegue
  // l'identità applicativa, quindi non finisce nel cestino del cliente e
  // l'unica traccia di chi l'ha chiesta è l'audit log del portale (§8.5).
  const [toDelete, setToDelete] = useState<Entry[] | null>(null);

  /*
   * Selezione multipla: si tengono i `ref`, non gli oggetti. Le entry arrivano
   * nuove a ogni refetch — dopo un caricamento, dopo una cancellazione — e
   * confrontare oggetti terrebbe selezionata una copia vecchia.
   */
  const [selezione, setSelezione] = useState<ReadonlySet<string>>(new Set());

  const entriesQui = browse.data?.entries;

  /*
   * La selezione non sopravvive al cambio di cartella ne' all'entrata in
   * ricerca: sono elenchi diversi, e un'azione cumulativa su file che non si
   * vedono piu' e' il modo piu' rapido per cancellare la cosa sbagliata.
   * Alla stessa ragione risponde la potatura dei ref spariti dopo un refetch.
   */
  useEffect(() => {
    setSelezione(new Set());
  }, [ref, searching]);

  useEffect(() => {
    if (!entriesQui) return;
    setSelezione((prima) => {
      if (prima.size === 0) return prima;
      const vivi = new Set(entriesQui.map((e) => e.ref));
      const dopo = new Set([...prima].filter((r) => vivi.has(r)));
      return dopo.size === prima.size ? prima : dopo;
    });
  }, [entriesQui]);

  const selezionati = (entriesQui ?? []).filter((e) => selezione.has(e.ref));

  const toggleSelect = useCallback((entry: Entry) => {
    setSelezione((prima) => {
      const dopo = new Set(prima);
      if (!dopo.delete(entry.ref)) dopo.add(entry.ref);
      return dopo;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const file = (entriesQui ?? []).filter((e) => e.kind !== 'folder');
    setSelezione((prima) =>
      file.length > 0 && file.every((e) => prima.has(e.ref))
        ? new Set()
        : new Set(file.map((e) => e.ref)),
    );
  }, [entriesQui]);

  /*
   * Scarico cumulativo: non esiste un endpoint che impacchetti in zip, quindi
   * sono N scarichi separati, uno ogni 250ms. Senza la pausa il browser tratta
   * la raffica come popup e ne lascia passare uno solo; la prima volta chiede
   * comunque il permesso per «piu' file», ed e' giusto che lo chieda.
   */
  const scaricaSelezionati = useCallback(() => {
    const scaricabili = selezionati.filter((e) => e.perms.download);
    scaricabili.forEach((entry, i) => {
      window.setTimeout(() => {
        const a = document.createElement('a');
        a.href = api.fileUrl(entry.ref);
        a.download = entry.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }, i * 250);
    });
  }, [selezionati]);

  const remove = useMutation({
    /*
     * In sequenza, non in parallelo: sono cancellazioni su SharePoint, e una
     * raffica di DELETE concorrenti si prende un 429 invece di andare piu'
     * veloce. Al primo errore ci si ferma — quello che e' passato resta
     * passato, il dialogo mostra l'errore e la lista si aggiorna: chi guarda
     * vede cosa e' rimasto e puo' riprovare su quello.
     */
    mutationFn: async (entries: Entry[]) => {
      for (const entry of entries) {
        await api.deleteNode(entry.ref, entry.kind);
      }
    },
    onSuccess: () => {
      setToDelete(null);
      setSelezione(new Set());
    },
    onSettled: () => {
      // Anche le ricerche: un file eliminato non deve restare fra i risultati
      // di una ricerca già eseguita.
      void queryClient.invalidateQueries({ queryKey: ['browse'] });
      void queryClient.invalidateQueries({ queryKey: ['search'] });
    },
  });

  if (me.activeClient === null) {
    return (
      <div className="card docs__placeholder">
        <h2>Seleziona un cliente</h2>
        <p>
          Scegli l'area documentale dal menu {sidebarNav ? 'a sinistra' : 'in alto'} per iniziare.
        </p>
      </div>
    );
  }

  const crumbs = browse.data?.breadcrumb ?? [];
  const here = crumbs.at(-1)?.name ?? me.activeClient.displayName;
  const entries = browse.data?.entries ?? [];
  const nFolders = entries.filter((e) => e.kind === 'folder').length;
  const nFiles = entries.length - nFolders;

  const breadcrumb = (
    <nav className="crumbs" aria-label="Percorso">
      {crumbs.map((crumb, i, all) => {
        const isLast = i === all.length - 1;
        return (
          <span key={crumb.ref} className="crumbs__item">
            {isLast ? (
              <span aria-current="page">{crumb.name}</span>
            ) : (
              <button type="button" onClick={() => setRef(i === 0 ? undefined : crumb.ref)}>
                {crumb.name}
              </button>
            )}
            {isLast ? null : <span className="crumbs__sep">/</span>}
          </span>
        );
      })}
    </nav>
  );

  const uploadButton = canUpload ? (
    <button type="button" className="btn" onClick={open}>
      Carica documenti
    </button>
  ) : null;

  const listContent = browse.isPending ? (
    <div className="docs__state">Caricamento…</div>
  ) : browse.isError ? (
    <BrowseError
      error={browse.error}
      isRoot={ref === undefined}
      isAdmin={me.user.isAdmin}
      onRetry={() => void browse.refetch()}
    />
  ) : stitch ? (
    <StitchList
      entries={browse.data.entries}
      selected={selezione}
      onToggleSelect={toggleSelect}
      onToggleSelectAll={toggleSelectAll}
      onOpenFolder={openFolder}
      onOpenLink={openLink}
      onDelete={(entry) => setToDelete([entry])}
    />
  ) : richContent ? (
    <FileGrid
      entries={browse.data.entries}
      onOpenFolder={openFolder}
      onDelete={(entry) => setToDelete([entry])}
    />
  ) : (
    <FileTable
      entries={browse.data.entries}
      onOpenFolder={openFolder}
      onDelete={(entry) => setToDelete([entry])}
    />
  );

  const searchContent = search.isPending ? (
    <div className="docs__state">Ricerca in corso…</div>
  ) : search.isError ? (
    <div className="docs__state">
      <div className="alert alert--error">
        {search.error instanceof ApiError ? search.error.message : 'Ricerca non riuscita.'}
      </div>
      <button type="button" className="btn btn--ghost" onClick={() => void search.refetch()}>
        Riprova
      </button>
    </div>
  ) : (
    <SearchResults
      query={search.data.query}
      results={search.data.results}
      truncated={search.data.truncated}
      clientName={me.activeClient.displayName}
      onOpenFolder={goToFolder}
      onOpenLink={openLink}
    />
  );

  const content = searching ? searchContent : listContent;

  const searchBox = (
    <SearchBox
      value={q}
      onChange={setQ}
      scope={scope}
      onScopeChange={setScope}
      folderName={crumbs.length > 1 ? here : null}
      busy={searching && search.isFetching}
    />
  );

  return (
    <div
      className={`docs${richContent ? ' docs--rich' : ''}${stitch ? ' docs--stitch' : ''}`}
      {...getRootProps()}
    >
      <input {...getInputProps()} />

      {richContent ? (
        <>
          <header className="docs__hero">
            <div className="docs__hero-text">
              {crumbs.length > 1 ? breadcrumb : null}
              <h1 className="docs__hero-title">{here}</h1>
              {/* Durante una ricerca il conteggio della cartella direbbe una
                  cosa diversa da quello che si sta guardando. */}
              {browse.data && !searching ? (
                <p className="docs__hero-meta">
                  {nFolders > 0 ? `${nFolders} ${nFolders === 1 ? 'cartella' : 'cartelle'}` : null}
                  {nFolders > 0 && nFiles > 0 ? ' · ' : null}
                  {nFiles > 0 ? `${nFiles} ${nFiles === 1 ? 'file' : 'file'}` : null}
                  {entries.length === 0 ? 'cartella vuota' : null}
                </p>
              ) : null}

              {/* Sotto al conteggio, in colonna con quello che filtra: la
                  casella si legge insieme a ciò su cui agisce, invece di stare
                  in riga con l'azione opposta. */}
              <div className="docs__hero-search">{searchBox}</div>
            </div>
            {uploadButton}
          </header>

          {/* Sopra l'elenco, non in fondo: e' un'azione sulla cartella
              corrente, come il pulsante di caricamento nell'intestazione, e in
              fondo a una cartella lunga bisognava scorrere fino in cima o fino
              in basso per trovarla. */}
          {searching ? null : canUpload ? (
            <button type="button" className="docs__dropzone" onClick={open}>
              <strong>Trascina qui i documenti</strong>
              <span>o fai clic per sceglierli — verranno caricati in «{here}»</span>
            </button>
          ) : browse.data ? (
            <p className="docs__hint docs__hint--muted">
              In questa cartella non è consentito caricare documenti.
            </p>
          ) : null}

          {/*
            La barra della selezione sta sopra l'elenco e compare solo quando
            c'e' qualcosa di scelto: e' un'azione sulle righe sotto, e da sotto
            (o peggio, in un angolo fisso) costringerebbe a cercarla.
          */}
          {searching || selezionati.length === 0 ? null : (
            <div className="docs__selbar" role="status">
              <span className="docs__selbar-conto">
                {selezionati.length}{' '}
                {selezionati.length === 1 ? 'file selezionato' : 'file selezionati'}
              </span>

              <div className="docs__selbar-azioni">
                {/* I permessi sono per singolo file: si agisce su quelli che li
                    hanno, e il conteggio sul pulsante dice quanti sono davvero
                    — un «Elimina» che ne cancella 3 su 5 senza dirlo mente. */}
                {selezionati.some((e) => e.perms.download) ? (
                  <button type="button" className="btn btn--sm" onClick={scaricaSelezionati}>
                    Scarica {selezionati.filter((e) => e.perms.download).length}
                  </button>
                ) : null}

                {selezionati.some((e) => e.perms.delete) ? (
                  <button
                    type="button"
                    className="btn btn--danger btn--sm"
                    onClick={() => setToDelete(selezionati.filter((e) => e.perms.delete))}
                  >
                    Elimina {selezionati.filter((e) => e.perms.delete).length}
                  </button>
                ) : null}

                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setSelezione(new Set())}
                >
                  Annulla
                </button>
              </div>
            </div>
          )}

          <div className="docs__body">{content}</div>
        </>
      ) : (
        <>
          <div className="docs__toolbar">
            {breadcrumb}
            {uploadButton}
          </div>

          {/* Qui non c'è un conteggio sotto cui stare: la casella prende una
              riga sua, sopra l'elenco. */}
          <div className="docs__searchrow">{searchBox}</div>

          {/* I risultati hanno già un contorno loro: infilarli nella card
              produrrebbe due bordi annidati. */}
          <div className={searching ? undefined : 'card docs__panel'}>{content}</div>

          {searching ? null : canUpload ? (
            <p className="docs__hint">
              Trascina i file in questa pagina per caricarli in <strong>{here}</strong>.
            </p>
          ) : browse.data ? (
            <p className="docs__hint docs__hint--muted">
              In questa cartella non è consentito caricare documenti.
            </p>
          ) : null}
        </>
      )}

      {/*
        Ripiego quando la scheda non si e' potuta aprire, e avviso quando il
        collegamento e' rotto o punta a uno schema che il server non apre.
        Un link vero, non un altro window.open: il click sull'ancora e' un
        gesto diretto e nessun blocco popup lo intercetta.
      */}
      {linkBloccato ? (
        <div className="picker" role="dialog" aria-modal="true" aria-label="Collegamento">
          <div className="picker__box card docs__link">
            <header className="picker__head">
              <h3>{linkBloccato.name}</h3>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setLinkBloccato(null)}
              >
                Chiudi
              </button>
            </header>
            <div className="docs__link-body">
              <p>Il browser ha bloccato l'apertura automatica. Il collegamento porta a:</p>
              <a
                className="docs__link-url"
                href={linkBloccato.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setLinkBloccato(null)}
              >
                {linkBloccato.url}
              </a>
            </div>
          </div>
        </div>
      ) : null}

      {linkErrore ? (
        <div className="docs__link-error alert alert--error" role="alert">
          <span>{linkErrore}</span>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setLinkErrore(null)}>
            Chiudi
          </button>
        </div>
      ) : null}

      {toDelete ? (
        <DeleteEntryDialog
          entries={toDelete}
          pending={remove.isPending}
          error={remove.error}
          onCancel={() => {
            setToDelete(null);
            remove.reset();
          }}
          onConfirm={() => remove.mutate(toDelete)}
        />
      ) : null}

      <UploadPanel
        items={uploads.items}
        onRetry={uploads.retry}
        onCancel={uploads.cancel}
        onClear={uploads.clearFinished}
      />

      {isDragActive && canUpload ? (
        <div className="docs__drop" aria-hidden="true">
          <div className="docs__drop-box">Rilascia i file per caricarli</div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Un 404 sulla radice non è "elemento non trovato": è "nessuna regola ti
 * concede di vedere niente". Il messaggio generico va bene per un cliente —
 * non gli si rivela la configurazione — ma a un admin va detto dove guardare,
 * altrimenti la diagnosi è un giro nei log.
 */
function BrowseError({
  error,
  isRoot,
  isAdmin,
  onRetry,
}: {
  error: unknown;
  isRoot: boolean;
  isAdmin: boolean;
  onRetry: () => void;
}) {
  const notFound = error instanceof ApiError && error.status === 404;
  const permessi = notFound && isRoot;

  return (
    <div className="docs__state">
      <div className="alert alert--error">
        {permessi
          ? "Nessun documento accessibile: il template di permessi di questa organizzazione non concede la visualizzazione."
          : error instanceof ApiError
            ? error.message
            : 'Impossibile caricare la cartella.'}
      </div>

      {permessi && isAdmin ? (
        <p className="docs__diag">
          Serve una regola che conceda <strong>vedere</strong> sulla radice — tipicamente il
          percorso <code>**</code>. Controlla in{' '}
          <Link to="/template">Template</Link>.
        </p>
      ) : null}

      <button type="button" className="btn btn--ghost" onClick={onRetry}>
        Riprova
      </button>
    </div>
  );
}
